import { useEffect, useState } from 'react';
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { client } from '~/client';
import { useAssets } from '~/components/AssetSelect';
import { CreateJobForm } from '~/components/job/CreateJobForm';
import { graphql } from '~/gql';

const CreateJobFormDocument = graphql(`
  query CreateJobForm {
    integrations
    attributes(entityType: "Site") {
      id
      type
      category
      name
      value
      condition
    }
    jobNameOptions
    jobTemplates(status: Active) {
      ...JobTemplateFields
    }
    locations(status: Active) {
      __typename
      id
      name
      ... on Asset {
        deployment {
          id
          displayLocation
          defaultSourceId
        }
      }
    }
    contacts(status: Active) {
      ...ContactFields
    }
  }
`);

export async function loader() {
  const { data, error } = await client
    .query(CreateJobFormDocument, {}, { requestPolicy: 'cache-first' })
    .toPromise();

  if (error || !data) {
    throw new Response(error?.message, { status: 500 });
  }

  return data;
}

type LoadingStep = {
  name: string;
  status: 'complete' | 'current' | 'upcoming';
};

function LoadingSteps({ currentStep }: { currentStep: number }) {
  const steps: LoadingStep[] = [
    { name: 'Loading asset details', status: 'upcoming' },
    { name: 'Preparing job form', status: 'upcoming' },
    { name: 'Setting up templates', status: 'upcoming' },
    { name: 'Ready!', status: 'upcoming' },
  ].map((step, index) => ({
    ...step,
    status:
      index < currentStep
        ? 'complete'
        : index === currentStep
        ? 'current'
        : 'upcoming',
  }));

  return (
    <div className='flex h-full w-full flex-col items-center justify-center'>
      <div className='mb-8'>
        <svg
          className='h-10 w-10 fill-brand stroke-brand transition-opacity duration-75'
          width='24'
          height='24'
          viewBox='0 0 24 24'
          xmlns='http://www.w3.org/2000/svg'
        >
          <style>
            {
              '.spinner_7NYg{animation:spinner_0KQs 1.2s cubic-bezier(0.52,.6,.25,.99) infinite}@keyframes spinner_0KQs{0%{r:0;opacity:1}100%{r:11px;opacity:0}}'
            }
          </style>
          <circle className='spinner_7NYg' cx='12' cy='12' r='0' />
        </svg>
      </div>

      <div className='text-center'>
        <h3 className='mb-4 text-lg font-medium text-gray-900'>
          Creating job from asset
        </h3>
        <p className='mb-8 text-sm text-gray-600'>
          {steps[currentStep]?.name || 'Loading...'}
        </p>

        <nav className='flex items-center justify-center' aria-label='Progress'>
          <ol className='flex items-center space-x-3'>
            {steps.map((step, index) => (
              <li key={step.name}>
                {step.status === 'complete' ? (
                  <div className='block h-2.5 w-2.5 rounded-full bg-brand'>
                    <span className='sr-only'>{step.name}</span>
                  </div>
                ) : step.status === 'current' ? (
                  <div className='relative flex items-center justify-center'>
                    <span
                      className='absolute flex h-5 w-5 p-px'
                      aria-hidden='true'
                    >
                      <span className='h-full w-full rounded-full bg-brand opacity-25' />
                    </span>
                    <span
                      className='relative block h-2.5 w-2.5 rounded-full bg-brand'
                      aria-hidden='true'
                    />
                    <span className='sr-only'>{step.name}</span>
                  </div>
                ) : (
                  <div className='block h-2.5 w-2.5 rounded-full bg-gray-200'>
                    <span className='sr-only'>{step.name}</span>
                  </div>
                )}
              </li>
            ))}
          </ol>
        </nav>
      </div>
    </div>
  );
}

export default function CreateJobFromSiteAssetRoute() {
  const data = useLoaderData() as Awaited<ReturnType<typeof loader>>;
  const navigate = useNavigate();
  const { siteId, assetId } = useParams();
  const location = useLocation();

  const [showLoading, setShowLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Get asset data from existing assets hook
  const [assets, , fetching, error] = useAssets();
  const asset = assets.find((a: (typeof assets)[number]) => a.id === assetId);
  const deployment = asset?.deployment;

  const initialAsset = asset
    ? {
        id: asset.id,
        name: asset.name,
        type: asset.type,
        deployment: deployment
          ? {
              location: deployment.location
                ? {
                    id: deployment.location.id,
                    name: deployment.location.name,
                  }
                : undefined,
              space: deployment.space
                ? {
                    id: deployment.space.id,
                    name: deployment.space.name,
                  }
                : undefined,
            }
          : undefined,
      }
    : undefined;

  // Show loading if asset is not found while data is being fetched
  useEffect(() => {
    if (fetching && !asset) {
      setShowLoading(true);

      const stepInterval = setInterval(() => {
        setCurrentStep((prev) => {
          if (prev >= 3) {
            clearInterval(stepInterval);
            return prev;
          }
          return prev + 1;
        });
      }, 2000);

      return () => clearInterval(stepInterval);
    } else {
      setShowLoading(false);
    }
  }, [fetching, asset]);

  if (fetching) {
    return <LoadingSteps currentStep={currentStep} />;
  }

  if (error) {
    return <div>Error loading assets: {error.message}</div>;
  }

  if (!asset && assets.length === 0) {
    return <div>No assets found</div>;
  }

  if (!asset) {
    return <div>Asset not found</div>;
  }

  return (
    <CreateJobForm
      data={data}
      animationSource='asset'
      locationId={deployment?.location?.id || siteId}
      initialAsset={initialAsset}
      onClose={() => {
        // Navigate back to the site asset view
        // Get the stored tab from sessionStorage
        const storedTab = assetId
          ? sessionStorage.getItem(`assetTab_${assetId}`)
          : null;
        const tabParam = storedTab ? `?tab=${storedTab}` : '';
        const targetPath = `../..${tabParam}`;

        navigate(targetPath);
      }}
      onSuccess={(data) => {
        // Always return to jobs tab with job highlighted
        const jobId = data?.createJobs?.[0]?.id;
        const targetUrl = jobId
          ? `../..?tab=1&highlightJob=${jobId}`
          : '../..?tab=1';

        navigate(targetUrl);
      }}
    />
  );
}
